import { Fragment, useEffect, useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  IconButton,
  InputAdornment,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { fetchFullProjects, updateProjectTask } from "../api/projects";
import { useAppContext } from "../context/AppContext";
import { useTranslation } from "react-i18next";
import SearchIcon from '@mui/icons-material/Search';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import { fetchCompanyEmployees } from "../api/users";
import { generateColorFromId } from "../utils/utilFunctions";
import React from "react";
import AddTimeLogDialog from "../components/dialogs/AddTimeLogDialog";
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationDialog from "../components/dialogs/ConfirmationDialog";
import { deleteTimeLog } from "../api/timeLog";
import { useLocation, useParams } from "react-router-dom";
import { CustomTextField } from "../components/OverrideComponents";


function Home() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { user, setIsLoading, setUser, setEmployeesAppContext } = useAppContext();
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [search, setSearch] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const location = useLocation();

  const getQueryParam = (param) => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get(param);
  };

  const queryTaskId = getQueryParam("task_id");

  const [selectedTaskId, setSelectedTaskId] = useState(null);

  const [expandedTaskId, setExpandedTaskId] = useState(parseInt(queryTaskId));

  useEffect(() => {
    setExpandedTaskId(parseInt(queryTaskId))
  }, [queryTaskId])

  const [employees, setEmployees] = useState(null);
  const [employeeMenuAnchorEl, setEmployeeMenuAnchorEl] = useState(null);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [selectedProjectsAnchorEl, setSelectedProjectsAnchorEl] = useState(null);

  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedCategoriesAnchorEl, setSelectedCategoriesAnchorEl] = useState(null);

  const [selectedPhases, setSelectedPhases] = useState([]);
  const [selectedPhasesAnchorEl, setSelectedPhasesAnchorEl] = useState(null);

  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedUsersAnchorEl, setSelectedUsersAnchorEl] = useState(null);

  const [openTimeLogDialog, setOpenTimeLogDialog] = useState(false);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [timeLogIdToDelete, setTimeLogIdToDelete] = useState(null);

  const handleEmployeeMenuOpen = (event, taskId) => {
    event.stopPropagation();
    setEmployeeMenuAnchorEl(event.currentTarget);
    setSelectedTaskId(taskId);

    const currentTask = tasks.find(task => task.id === taskId);
    if (currentTask) {
      setSelectedEmployeeId(currentTask.user_id); // Set the current task's user_id as selected employee
    }
  };

  const handleEmployeeMenuClose = () => {
    setEmployeeMenuAnchorEl(null);
  };

  async function loadProjects() {
    try {
      const data = await fetchFullProjects();
      setProjects(data.projectsWithDetails);
      setTasks(data.tasks);
    } catch (err) {
      console.log('error: ', err)
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    async function loadEmployees() {
      try {
        const data = await fetchCompanyEmployees(setEmployeesAppContext);
        setEmployees([...data, {
          id: 0,
          name: 'Unassigned',
          email: '',
          role: 'none',
          avatar: null,
          isActive: false,
        }]);
        setSelectedUsers([...data, {
          id: 0,
          name: 'Unassigned',
          email: '',
          role: 'none',
          avatar: null,
          isActive: false,
        }].map((employee) => employee.id));
      } catch (err) {
        console.log('error: ', err)
      } finally {
        setIsLoading(false);
      }
    }

    loadEmployees();
    setIsLoading(true);
    loadProjects();
  }, []);

  const handleMenuOpen = (event, taskId) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedTaskId(taskId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTaskId(null);
  };


  const handleStatusChange = async (status) => {
    const updatedTask = tasks.find((task) => task.id === selectedTaskId); // Find the task to update
    if (updatedTask) {
      updatedTask.status = status;
      console.log('updatedTask: ', updatedTask);
      await updateProjectTask(updatedTask.id, { ...updatedTask, deadline: updatedTask.deadline?.split("T")[0] ?? null })
    }
    handleMenuClose();
  };

  const handleEmployeeChange = async (employeeId) => {
    const updatedTask = tasks.find((task) => task.id === selectedTaskId); // Find the task to update
    if (updatedTask) {
      updatedTask.user_id = employeeId === 0 ? null : employeeId;
      await updateProjectTask(updatedTask.id, { ...updatedTask, deadline: updatedTask.deadline?.split("T")[0] ?? null })
    }
    handleEmployeeMenuClose();
  };

  const handleClickProjectsSelectMenu = (event) => {
    setSelectedProjectsAnchorEl(event.currentTarget);
  };

  const handleCloseProjectsSelectMenu = () => {
    setSelectedProjectsAnchorEl(null);
  };

  const handleToggleProject = (projectId) => {
    setSelectedCategories([]);
    setSelectedPhases([]);
    setSelectedProjects((prevSelected) =>
      prevSelected.includes(projectId)
        ? prevSelected.filter((id) => id !== projectId)
        : [...prevSelected, projectId]
    );
  };

  const handleClickCategoriesSelectMenu = (event) => {
    setSelectedCategoriesAnchorEl(event.currentTarget);
  };

  const handleCloseCategoriesSelectMenu = () => {
    setSelectedCategoriesAnchorEl(null);
  };

  const handleToggleCategory = (categoryId) => {
    setSelectedCategories((prevSelected) =>
      prevSelected.includes(categoryId)
        ? prevSelected.filter((id) => id !== categoryId)
        : [...prevSelected, categoryId]
    );
  };



  const handleClickPhasesSelectMenu = (event) => {
    setSelectedPhasesAnchorEl(event.currentTarget);
  };

  const handleClosePhasesSelectMenu = () => {
    setSelectedPhasesAnchorEl(null);
  };

  const handleTogglePhases = (phaseId) => {
    setSelectedPhases((prevSelected) =>
      prevSelected.includes(phaseId)
        ? prevSelected.filter((id) => id !== phaseId)
        : [...prevSelected, phaseId]
    );
  };


  const handleClickUsersSelectMenu = (event) => {
    console.log('open')
    setSelectedUsersAnchorEl(event.currentTarget);
  };

  const handleCloseUsersSelectMenu = () => {
    console.log('close')
    setSelectedUsersAnchorEl(null);
  };

  const handleToggleUsers = (userId) => {
    setSelectedUsers((prevSelected) =>
      prevSelected.includes(userId)
        ? prevSelected.filter((id) => id !== userId)
        : [...prevSelected, userId]
    );
  };

  const handleOpenTimeLogDialog = (taskId) => {
    setSelectedTaskId(taskId);
    setOpenTimeLogDialog(true);
  }

  const handleDialogClose = () => {
    setOpenTimeLogDialog(false);
    setSelectedTaskId(null);
  };

  const handleCraeteTimeLog = () => {
    loadProjects();
    setOpenTimeLogDialog(false);
    setSelectedTaskId(null);
  };

  const handleConfirmDeleteTimelog = async () => {
    await deleteTimeLog(timeLogIdToDelete);
    loadProjects();
    setTimeLogIdToDelete(null)
  }

  const handleToggleExpand = (taskId) => {
    setExpandedTaskId((prev) => (prev === taskId ? null : taskId));
  };

  return (
    <Box
      backgroundColor="white"
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh", // Full screen height
      }}
    >
      {/* Header or any content with fixed height */}
      <Box sx={{ padding: 2, height: 5 }}>
        <Typography variant="h5">{t('templates.tasks')}</Typography>
      </Box>

      {/* This will take the remaining height */}
      <Box
        sx={{
          backgroundColor: "#eee",
          margin: 2,
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
          height: "100%", // assume parent gives this height or use a specific height like 400
          overflow: "hidden",
        }}
      >
        {/* Header */}
        {!isMobile ? (
          <Box sx={{ height: 60, px: 1, display: "flex", alignItems: "center" }}>
            <TextField
              variant="outlined"
              size="small"
              label="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                width: 250,
                '& .MuiInputBase-input': {
                  fontSize: '0.875rem', // Smaller font size
                  color: 'black', // Text color black
                },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white', // Background color white
                  '& fieldset': {
                    borderColor: 'gray', // Default border color gray
                  },
                  '&:hover fieldset': {
                    borderColor: 'gray', // Border color on hover stays gray
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'darkgray', // Border color when focused (selected) is dark gray
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'black', // Label color black
                },
                '& .Mui-focused .MuiInputLabel-root': {
                  color: 'black', // Keep label color black when focused
                },
              }}
              // InputLabelProps={{
              //   shrink: search !== '', // This will shrink the label when there is a value
              // }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'darkgray' }} />
                  </InputAdornment>
                ),
              }}
            />

            {employees && (
              <Box sx={{
                display: 'flex',
                flexDirection: 'columns',
                marginLeft: 2,
              }} >
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'columns',
                  cursor: 'pointer',
                }} onClick={handleClickUsersSelectMenu} >
                  {selectedUsers.length === 0 && (
                    <Typography>{t('common.no_user_selected')}</Typography>
                  )}
                  {selectedUsers.map((userId, index) => {
                    const employee = employees.find((emp) => emp.id === userId);

                    // Handle unassigned
                    if (userId === 0 || !employee) {
                      return (
                        <Avatar
                          key={`userSelect-unassigned`}
                          sx={{
                            width: 30,
                            height: 30,
                            marginLeft: index > 0 ? "-17px" : 0,
                            color: 'black',
                            cursor: 'pointer',
                            backgroundColor: 'gray',
                          }}
                        >
                        </Avatar>
                      );
                    }

                    // Handle regular employees
                    return (
                      <Avatar
                        key={`useSelect${employee.id}${employee.name}`}
                        sx={{
                          width: 30,
                          height: 30,
                          marginLeft: index > 0 ? "-17px" : 0,
                          color: 'black',
                          cursor: 'pointer',
                          backgroundColor: generateColorFromId(`${employee.id}${employee.id}${employee.id}`),
                        }}
                      >
                        {employee.name[0]}
                      </Avatar>
                    );
                  })}
                </Box>

                <Menu
                  key={selectedUsersAnchorEl ? selectedUsersAnchorEl.id : 'closed'}
                  anchorEl={selectedUsersAnchorEl}
                  open={Boolean(selectedUsersAnchorEl)}
                  onClose={handleCloseUsersSelectMenu}
                >
                  {employees.map((employee) => (
                    <MenuItem key={employee.id} onClick={() => handleToggleUsers(employee.id)}>
                      <Checkbox
                        sx={{
                          color: 'black', // Set the checkbox color to black
                          '&.Mui-checked': {
                            color: 'black', // Ensure the color stays black when checked
                          },
                        }}
                        checked={selectedUsers.includes(employee.id)}
                      />
                      <ListItemText sx={{ color: 'black' }} primary={employee.name} />
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            )}

            <Button
              variant="outlined"
              onClick={handleClickProjectsSelectMenu}
              sx={{
                marginLeft: 2,
                display: 'flex',
                alignItems: 'center',
                color: 'black', // Text color black
                backgroundColor: 'white', // Set white background
                border: 'none', // Remove border
                position: 'relative', // Allow the badge to be positioned absolutely
                '&:hover': {
                  border: 'none', // Ensure no border on hover as well
                  backgroundColor: 'white', // Ensure background remains white on hover
                }
              }}
            >
              {t('common.projects')}
              <Badge
                badgeContent={selectedProjects.length || 0}
                color="default" // No primary color, will style manually
                sx={{
                  position: 'absolute', // Position badge absolutely
                  top: 2, // Adjust top to position it above the button
                  right: 2, // Adjust right to position it on the top right
                  backgroundColor: 'black', // Set black background for the badge container
                  color: 'white', // Set white text color
                  '& .MuiBadge-dot': {
                    backgroundColor: 'black', // Ensure dot is black
                  },
                  '& .MuiBadge-dot, .MuiBadge-standard': {
                    backgroundColor: 'black', // Override background color
                  },
                }}
              />
            </Button>

            <Menu
              anchorEl={selectedProjectsAnchorEl}
              open={Boolean(selectedProjectsAnchorEl)}
              onClose={handleCloseProjectsSelectMenu}
            >
              {projects.map((project) => (
                <MenuItem key={project.id} onClick={() => handleToggleProject(project.id)}>
                  <Checkbox
                    sx={{
                      color: 'black', // Set the checkbox color to black
                      '&.Mui-checked': {
                        color: 'black', // Ensure the color stays black when checked
                      },
                    }}
                    checked={selectedProjects.includes(project.id)}
                  />
                  <ListItemText sx={{ color: 'black' }} primary={project.title} />
                </MenuItem>
              ))}
            </Menu>

            {selectedProjects.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                <Button
                  variant="outlined"
                  onClick={handleClickCategoriesSelectMenu}
                  sx={{
                    marginLeft: 2,
                    display: 'flex',
                    alignItems: 'center',
                    color: 'black',
                    backgroundColor: 'white',
                    border: 'none',
                    position: 'relative',
                    '&:hover': {
                      border: 'none',
                      backgroundColor: 'white',
                    }
                  }}
                >
                  {t('common.categories')}
                  <Badge
                    badgeContent={selectedCategories.length || 0}
                    color="default"
                    sx={{
                      position: 'absolute',
                      top: 2,
                      right: 2,
                      backgroundColor: 'black',
                      color: 'white',
                      '& .MuiBadge-dot': {
                        backgroundColor: 'black',
                      },
                      '& .MuiBadge-dot, .MuiBadge-standard': {
                        backgroundColor: 'black',
                      },
                    }}
                  />
                </Button>

                <Menu
                  anchorEl={selectedCategoriesAnchorEl}
                  open={Boolean(selectedCategoriesAnchorEl)}
                  onClose={handleCloseCategoriesSelectMenu}
                >
                  {selectedProjects.map((projectId) => {
                    const project = projects.find((proj) => proj.id === projectId);
                    const categories = project ? project.categories : []; // Get categories of the selected project

                    return (
                      <Box key={projectId}>
                        <MenuItem sx={{ color: 'black' }} disabled>{project?.title}</MenuItem> {/* Show the project title */}
                        {/* Render categories for each project */}
                        {categories.map((category) => (
                          <MenuItem key={category.id} onClick={() => handleToggleCategory(category.id, projectId)}>
                            <Checkbox
                              sx={{
                                color: 'black',
                                '&.Mui-checked': {
                                  color: 'black',
                                },
                              }}
                              checked={selectedCategories.includes(category.id)}
                            />
                            <ListItemText sx={{ color: 'black' }} primary={category.title} />
                          </MenuItem>
                        ))}
                      </Box>
                    );
                  })}
                </Menu>

                <Button
                  variant="outlined"
                  onClick={handleClickPhasesSelectMenu}
                  sx={{
                    marginLeft: 2,
                    display: 'flex',
                    alignItems: 'center',
                    color: 'black',
                    backgroundColor: 'white',
                    border: 'none',
                    position: 'relative',
                    '&:hover': {
                      border: 'none',
                      backgroundColor: 'white',
                    }
                  }}
                >
                  {t('common.phases')}
                  <Badge
                    badgeContent={selectedPhases.length || 0}
                    color="default"
                    sx={{
                      position: 'absolute',
                      top: 2,
                      right: 2,
                      backgroundColor: 'black',
                      color: 'white',
                      '& .MuiBadge-dot': {
                        backgroundColor: 'black',
                      },
                      '& .MuiBadge-dot, .MuiBadge-standard': {
                        backgroundColor: 'black',
                      },
                    }}
                  />
                </Button>

                <Menu
                  anchorEl={selectedPhasesAnchorEl}
                  open={Boolean(selectedPhasesAnchorEl)}
                  onClose={handleClosePhasesSelectMenu}
                >
                  {selectedProjects.map((projectId) => {
                    const project = projects.find((proj) => proj.id === projectId);
                    const phases = project ? project.phases : []; // Get categories of the selected project

                    return (
                      <Box key={projectId}>
                        <MenuItem sx={{ color: 'black' }} disabled>{project?.title}</MenuItem> {/* Show the project title */}
                        {/* Render categories for each project */}
                        {phases.map((phase) => (
                          <MenuItem key={phase.id} onClick={() => handleTogglePhases(phase.id, projectId)}>
                            <Checkbox
                              sx={{
                                color: 'black',
                                '&.Mui-checked': {
                                  color: 'black',
                                },
                              }}
                              checked={selectedPhases.includes(phase.id)}
                            />
                            <ListItemText sx={{ color: 'black' }} primary={phase.title} />
                          </MenuItem>
                        ))}
                      </Box>
                    );
                  })}
                </Menu>
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{ height: 150, px: 1, display: "flex", alignItems: "center", flexDirection: "column" }}>
            <Box sx={{ marginTop: 1, display: "flex" }}>
              <CustomTextField
                variant="outlined"
                size="small"
                label="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'darkgray' }} />
                    </InputAdornment>
                  ),
                }}
              />

              {employees && (
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'columns',
                  marginLeft: 2,
                }} >
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'columns',
                    cursor: 'pointer',
                  }} onClick={handleClickUsersSelectMenu} >
                    {selectedUsers.length === 0 && (
                      <Typography>{t('common.no_user_selected')}</Typography>
                    )}
                    {selectedUsers.map((userId, index) => {
                      const employee = employees.find((emp) => emp.id === userId);

                      // Handle unassigned
                      if (userId === 0 || !employee) {
                        return (
                          <Avatar
                            key={`userSelect-unassigned`}
                            sx={{
                              width: 30,
                              height: 30,
                              marginLeft: index > 0 ? "-17px" : 0,
                              color: 'black',
                              cursor: 'pointer',
                              backgroundColor: 'gray',
                            }}
                          >
                          </Avatar>
                        );
                      }

                      // Handle regular employees
                      return (
                        <Avatar
                          key={`useSelect${employee.id}${employee.name}`}
                          sx={{
                            width: 30,
                            height: 30,
                            marginLeft: index > 0 ? "-17px" : 0,
                            color: 'black',
                            cursor: 'pointer',
                            backgroundColor: generateColorFromId(`${employee.id}${employee.id}${employee.id}`),
                          }}
                        >
                          {employee.name[0]}
                        </Avatar>
                      );
                    })}
                  </Box>

                  <Menu
                    key={selectedUsersAnchorEl ? selectedUsersAnchorEl.id : 'closed'}
                    anchorEl={selectedUsersAnchorEl}
                    open={Boolean(selectedUsersAnchorEl)}
                    onClose={handleCloseUsersSelectMenu}
                  >
                    {employees.map((employee) => (
                      <MenuItem key={employee.id} onClick={() => handleToggleUsers(employee.id)}>
                        <Checkbox
                          sx={{
                            color: 'black', // Set the checkbox color to black
                            '&.Mui-checked': {
                              color: 'black', // Ensure the color stays black when checked
                            },
                          }}
                          checked={selectedUsers.includes(employee.id)}
                        />
                        <ListItemText sx={{ color: 'black' }} primary={employee.name} />
                      </MenuItem>
                    ))}
                  </Menu>
                </Box>
              )}
            </Box>

            <Box sx={{ marginTop: 1, display: "flex" }}>
              <Button
                variant="outlined"
                onClick={handleClickProjectsSelectMenu}
                sx={{
                  marginLeft: 2,
                  display: 'flex',
                  alignItems: 'center',
                  color: 'black', // Text color black
                  backgroundColor: 'white', // Set white background
                  border: 'none', // Remove border
                  position: 'relative', // Allow the badge to be positioned absolutely
                  '&:hover': {
                    border: 'none', // Ensure no border on hover as well
                    backgroundColor: 'white', // Ensure background remains white on hover
                  }
                }}
              >
                {t('common.projects')}
                <Badge
                  badgeContent={selectedProjects.length || 0}
                  color="default" // No primary color, will style manually
                  sx={{
                    position: 'absolute', // Position badge absolutely
                    top: 2, // Adjust top to position it above the button
                    right: 2, // Adjust right to position it on the top right
                    backgroundColor: 'black', // Set black background for the badge container
                    color: 'white', // Set white text color
                    '& .MuiBadge-dot': {
                      backgroundColor: 'black', // Ensure dot is black
                    },
                    '& .MuiBadge-dot, .MuiBadge-standard': {
                      backgroundColor: 'black', // Override background color
                    },
                  }}
                />
              </Button>

              <Menu
                anchorEl={selectedProjectsAnchorEl}
                open={Boolean(selectedProjectsAnchorEl)}
                onClose={handleCloseProjectsSelectMenu}
              >
                {projects.map((project) => (
                  <MenuItem key={project.id} onClick={() => handleToggleProject(project.id)}>
                    <Checkbox
                      sx={{
                        color: 'black', // Set the checkbox color to black
                        '&.Mui-checked': {
                          color: 'black', // Ensure the color stays black when checked
                        },
                      }}
                      checked={selectedProjects.includes(project.id)}
                    />
                    <ListItemText sx={{ color: 'black' }} primary={project.title} />
                  </MenuItem>
                ))}
              </Menu>
            </Box>
            <Box sx={{ marginTop: 1, display: "flex" }}>
              <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                <Button
                  variant="outlined"
                  disabled={!selectedProjects.length > 0}
                  onClick={handleClickCategoriesSelectMenu}
                  sx={{
                    marginLeft: 2,
                    display: 'flex',
                    alignItems: 'center',
                    color: 'black',
                    backgroundColor: 'white',
                    border: 'none',
                    position: 'relative',
                    '&:hover': {
                      border: 'none',
                      backgroundColor: 'white',
                    },
                    '&.Mui-disabled': {
                      color: 'gray', // Text color when disabled
                      backgroundColor: 'lightgray', // Background color when disabled
                      borderColor: 'gray', // Border color when disabled
                      cursor: 'not-allowed', // Show not-allowed cursor to indicate the button is disabled
                    }
                  }}
                >
                  {t('common.categories')}
                  <Badge
                    badgeContent={selectedCategories.length || 0}
                    color="default"
                    sx={{
                      position: 'absolute',
                      top: 2,
                      right: 2,
                      backgroundColor: 'black',
                      color: 'white',
                      '& .MuiBadge-dot': {
                        backgroundColor: 'black',
                      },
                      '& .MuiBadge-dot, .MuiBadge-standard': {
                        backgroundColor: 'black',
                      },
                    }}
                  />
                </Button>

                <Menu
                  anchorEl={selectedCategoriesAnchorEl}
                  open={Boolean(selectedCategoriesAnchorEl)}
                  onClose={handleCloseCategoriesSelectMenu}
                >
                  {selectedProjects.map((projectId) => {
                    const project = projects.find((proj) => proj.id === projectId);
                    const categories = project ? project.categories : []; // Get categories of the selected project

                    return (
                      <Box key={projectId}>
                        <MenuItem sx={{ color: 'black' }} disabled>{project?.title}</MenuItem> {/* Show the project title */}
                        {/* Render categories for each project */}
                        {categories.map((category) => (
                          <MenuItem key={category.id} onClick={() => handleToggleCategory(category.id, projectId)}>
                            <Checkbox
                              sx={{
                                color: 'black',
                                '&.Mui-checked': {
                                  color: 'black',
                                },
                              }}
                              checked={selectedCategories.includes(category.id)}
                            />
                            <ListItemText sx={{ color: 'black' }} primary={category.title} />
                          </MenuItem>
                        ))}
                      </Box>
                    );
                  })}
                </Menu>

                <Button
                  variant="outlined"
                  onClick={handleClickPhasesSelectMenu}
                  disabled={!selectedProjects.length > 0}
                  sx={{
                    marginLeft: 2,
                    display: 'flex',
                    alignItems: 'center',
                    color: 'black',
                    backgroundColor: 'white',
                    border: 'none',
                    position: 'relative',
                    '&:hover': {
                      border: 'none',
                      backgroundColor: 'white',
                    },
                    '&.Mui-disabled': {
                      color: 'gray', // Text color when disabled
                      backgroundColor: 'lightgray', // Background color when disabled
                      borderColor: 'gray', // Border color when disabled
                      cursor: 'not-allowed', // Show not-allowed cursor to indicate the button is disabled
                    }
                  }}
                >
                  {t('common.phases')}
                  <Badge
                    badgeContent={selectedPhases.length || 0}
                    color="default"
                    sx={{
                      position: 'absolute',
                      top: 2,
                      right: 2,
                      backgroundColor: 'black',
                      color: 'white',
                      '& .MuiBadge-dot': {
                        backgroundColor: 'black',
                      },
                      '& .MuiBadge-dot, .MuiBadge-standard': {
                        backgroundColor: 'black',
                      },
                    }}
                  />
                </Button>

                <Menu
                  anchorEl={selectedPhasesAnchorEl}
                  open={Boolean(selectedPhasesAnchorEl)}
                  onClose={handleClosePhasesSelectMenu}
                >
                  {selectedProjects.map((projectId) => {
                    const project = projects.find((proj) => proj.id === projectId);
                    const phases = project ? project.phases : []; // Get categories of the selected project

                    return (
                      <Box key={projectId}>
                        <MenuItem sx={{ color: 'black' }} disabled>{project?.title}</MenuItem> {/* Show the project title */}
                        {/* Render categories for each project */}
                        {phases.map((phase) => (
                          <MenuItem key={phase.id} onClick={() => handleTogglePhases(phase.id, projectId)}>
                            <Checkbox
                              sx={{
                                color: 'black',
                                '&.Mui-checked': {
                                  color: 'black',
                                },
                              }}
                              checked={selectedPhases.includes(phase.id)}
                            />
                            <ListItemText sx={{ color: 'black' }} primary={phase.title} />
                          </MenuItem>
                        ))}
                      </Box>
                    );
                  })}
                </Menu>
              </Box>
            </Box>
          </Box>
        )}

        {/* Scrollable Content */}
        <Box
          sx={{
            flex: 1,
            backgroundColor: "white",
            overflowY: "auto",
            marginLeft: '8px',
            marginRight: '8px',
            border: '1px solid lightgray',
            borderRadius: 1,
          }}
        >
          {tasks
            .filter((task) => {
              const isMatchingSearch = task.title.toLowerCase().includes(search.toLowerCase());
              const isMatchingProject = selectedProjects.length === 0 || selectedProjects.includes(task.project_id);
              const isMatchingPhase = selectedPhases.length === 0 || selectedPhases.includes(task.project_phase_id); // Filter by selected phases
              const isMatchingCategory = selectedCategories.length === 0 || selectedCategories.includes(task.project_category_id); // Filter by selected categories
              const isMatchingUser =
                selectedUsers.length === 0 ||
                selectedUsers.includes(task.user_id) ||
                (selectedUsers.includes(0) && task.user_id === null);

              // Combine all filters
              return isMatchingSearch && isMatchingProject && isMatchingPhase && isMatchingCategory && isMatchingUser;
            })
            .sort((a, b) => {
              // Sorting by status: 'completed' comes last, but other statuses remain in the same order
              if (a.status === 'completed' && b.status !== 'completed') return 1; // a is 'completed', b is not
              if (b.status === 'completed' && a.status !== 'completed') return -1; // b is 'completed', a is not

              // Sorting by deadline (earliest first)
              const aDeadline = a.deadline ? new Date(a.deadline) : null;
              const bDeadline = b.deadline ? new Date(b.deadline) : null;

              if (aDeadline && bDeadline) {
                return aDeadline - bDeadline; // Compare dates
              }
              if (aDeadline) return -1; // If only 'a' has a deadline, it comes first
              if (bDeadline) return 1; // If only 'b' has a deadline, it comes first
              return 0; // If neither have deadlines, don't change order
            })
            .map((task) => {
              const project = projects.find((proj) => proj.id === task.project_id);
              const projectPhase = project ? project.phases.find(phase => phase.id === task.project_phase_id) : null;
              const projectCategory = project ? project.categories.find(category => category.id === task.project_category_id) : null;
              const isExpanded = expandedTaskId === task.id;

              return <Box id={task.id} key={task.id} sx={{ display: 'inline-flex', minWidth: '100%' }}>
                <Box sx={{ minWidth: '100%' }}>
                  <Box sx={{ minWidth: '100%' }}>
                    <Box onClick={() => handleToggleExpand(task.id)}
                      sx={{ cursor: "pointer", px: 2, py: 1, display: "flex", flex: 1, width: 'calc(100% - 32px)', justifyContent: 'space-between', flexDirection: "rows" }}>
                      <Box sx={{ display: "flex" }}>
                        <Typography sx={{ width: '70px', maxWidth: "70px", display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>{task.title}</Typography>
                        {project && (
                          <Box sx={{
                            width: '70px',
                            maxWidth: '70px',
                            minWidth: '70px',
                            display: 'flex',
                            alignItems: 'center'
                          }}>
                            <Typography
                              variant="body2"
                              sx={{
                                width: '70px',
                                maxWidth: '70px',
                                minWidth: '70px',
                                marginLeft: 1,
                                fontStyle: 'italic',
                                color: 'gray',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                              }}
                            >
                              ({project.title})
                            </Typography>
                          </Box>
                        )}
                        {projectPhase && (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography
                              variant="body2"
                              sx={{
                                width: '70px',
                                maxWidth: '70px',
                                marginLeft: 1,
                                fontStyle: 'italic',
                                color: 'gray',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                              }}
                            >
                              ({projectPhase.title})
                            </Typography>
                          </Box>
                        )}
                        {projectCategory && (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography
                              variant="body2"
                              sx={{
                                width: '70px',
                                maxWidth: '70px',
                                marginLeft: 1,
                                fontStyle: 'italic',
                                color: 'gray',
                                display: 'block',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                              }}
                            >
                              ({projectCategory.title})
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, gap: 1 }}>
                        <Chip
                          label={t(`task_statuses.${task.status}`)}
                          size="small"
                          onClick={(event) => handleMenuOpen(event, task.id)}
                          sx={{
                            width: '120px', maxWidth: "120px",
                            backgroundColor: 'lightgray',
                            color: 'black',
                            borderRadius: '16px',
                            '&:hover': {
                              backgroundColor: 'darkgray', // Change background to darkgray on hover
                            },
                          }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'center', width: '70px', maxWidth: "70px" }}>
                          {task.estimated_hours && (
                            <Chip
                              icon={<HourglassEmptyIcon fontSize="small" sx={{ color: 'black' }} />}
                              label={`${task.estimated_hours}${t('common.hours')}`}
                              size="small"
                              sx={{
                                width: '70px', maxWidth: "70px",
                                backgroundColor: 'lightgray',
                                color: 'black',
                                borderRadius: '16px',
                                '& .MuiChip-icon': {  // This targets the icon within the Chip
                                  color: 'black',  // Ensures the icon color is black
                                }
                              }}
                            >
                            </Chip>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'center', width: '70px', maxWidth: "90px" }}>
                          {task.total_time_logged > -1 && (
                            <Chip
                              icon={<AccessTimeFilledIcon fontSize="small" sx={{ color: 'black' }} />}
                              label={`
                              ${Math.floor(task.total_time_logged / 60)}h 
                              ${task.total_time_logged % 60}m
                            `}
                              size="small"
                              sx={{
                                width: '90px', maxWidth: "90px",
                                backgroundColor: 'lightgray',
                                color: 'black',
                                borderRadius: '16px',
                                '& .MuiChip-icon': {  // This targets the icon within the Chip
                                  color: 'black',  // Ensures the icon color is black
                                }
                              }}
                            >
                            </Chip>
                          )}
                        </Box>

                        <Typography sx={{ width: '150px', justifyContent: 'center', display: 'flex', alignItems: 'center', fontSize: '0.8rem' }} color="textSecondary">
                          <span
                            style={{
                              color: task.deadline ? 'red' : 'inherit', // Change color to red for deadlines
                            }}
                          >
                            {task.deadline ? new Date(task.deadline).toLocaleDateString() : t('common.no_deadline')}
                          </span>
                        </Typography>
                        <Avatar
                          sx={{
                            width: 24,
                            height: 24,
                            color: 'black',
                            cursor: 'pointer',
                            backgroundColor: employees && employees.find((emp) => emp.id === task.user_id)
                              ? generateColorFromId(`${employees.find((emp) => emp.id === task.user_id).id}${employees.find((emp) => emp.id === task.user_id).id}${employees.find((emp) => emp.id === task.user_id).id}`)
                              : 'gray', // Default color if no employee is found
                          }}
                          onClick={(event) => handleEmployeeMenuOpen(event, task.id)}
                        >
                          {employees && employees.find((emp) => emp.id === task.user_id)?.name[0]}
                        </Avatar>
                      </Box>
                      <IconButton
                        onClick={() => handleOpenTimeLogDialog(task.id)}
                        aria-label="Log time"
                        sx={{
                          mt: 1,
                          marginLeft: 1,
                          backgroundColor: 'white',
                          border: '1px solid #ccc',
                          color: 'black',
                          width: 24,
                          height: 24,
                          '&:hover': {
                            backgroundColor: '#f5f5f5',
                            borderColor: '#999',
                          },
                          '&:active': {
                            backgroundColor: '#e0e0e0',
                            borderColor: '#777',
                          },
                        }}
                      >
                        <AccessTimeRoundedIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Box sx={{ px: 2, py: 1, backgroundColor: '#f9f9f9' }}>
                        {task.time_logs && task.time_logs.length > 0 ? (
                          <Box>
                            <Typography variant="subtitle2" gutterBottom>
                              {t("common.log_time")}:
                            </Typography>
                            {task.time_logs.map((log) => {
                              const employee = employees.find(emp => emp.id === log.user_id); // Find the employee by user_id
                              const canDelete =
                                user &&
                                (['owner', 'admin', 'manager'].includes(user.role) || user.id === log.user_id);

                              return (
                                <Box key={log.id} sx={{ mb: 1, pl: 1, display: 'flex', alignItems: 'center' }}>

                                  <Typography variant="body2">
                                    •
                                  </Typography>
                                  {employee && (
                                    <Avatar
                                      sx={{
                                        width: 30,
                                        height: 30,
                                        marginLeft: '8px',
                                        marginRight: '8px',
                                        color: 'black',
                                        cursor: 'pointer',
                                        backgroundColor: generateColorFromId(`${employee.id}${employee.id}${employee.id}`),
                                      }}
                                    >
                                      {employee.name[0]} {/* Display first letter of employee's name */}
                                    </Avatar>
                                  )}
                                  <Typography variant="body2">
                                    {new Date(log.logged_date).toLocaleDateString()} —
                                    {Math.floor(log.logged_minutes / 60)}h {log.logged_minutes % 60}m
                                    ({log.logged_minutes} min)
                                  </Typography>
                                  {canDelete && (
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => { setTimeLogIdToDelete(log.id); setOpenConfirmationDialog(true) }}
                                      sx={{ ml: 1 }}
                                    >
                                      <CloseIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                </Box>
                              );
                            })}
                            <Typography sx={{ fontWeight: 'bold', mt: 1 }}>
                              Total: {Math.floor(task.total_time_logged / 60)}h {task.total_time_logged % 60}m ({task.total_time_logged} min)
                            </Typography>

                          </Box>
                        ) : (
                          <Typography>{t('common.no_time_logged')}</Typography>
                        )}
                      </Box>
                    </Collapse>
                  </Box>
                  <Divider sx={{ width: "100%", backgroundColor: 'lightgray' }} />
                </Box>
              </Box>
            })}
        </Box>

        {/* Footer */}
        <Box sx={{ height: 30, px: 1, display: "flex", alignItems: "center" }}>
        </Box>
      </Box>
      {/* Menu for changing task status */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {['to_do', 'in_progress', 'blocked', 'needs_review', 'completed'].map((status) => (
          <MenuItem key={status} sx={{
            color: 'black',
            '&:hover': {
              backgroundColor: 'darkgray', // Change background to darkgray on hover
            },
          }} onClick={() => handleStatusChange(status)}>
            {t(`task_statuses.${status}`)}
          </MenuItem>
        ))}
      </Menu>
      <Menu
        anchorEl={employeeMenuAnchorEl}
        open={Boolean(employeeMenuAnchorEl)}
        onClose={handleEmployeeMenuClose}
      >
        {employees && employees.map((employee) => (
          <MenuItem
            key={employee.id}
            onClick={() => handleEmployeeChange(employee.id)}
            selected={employee.id === selectedEmployeeId}
          >
            <Typography sx={{ display: 'flex', alignItems: 'center', color: 'black' }}>
              {employee.name}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
      <AddTimeLogDialog
        open={openTimeLogDialog}
        onClose={handleDialogClose}
        task_id={selectedTaskId}
        onSuccess={handleCraeteTimeLog}>
      </AddTimeLogDialog>
      <ConfirmationDialog
        open={openConfirmationDialog}
        onClose={() => {
          setTimeLogIdToDelete(null);
          setOpenConfirmationDialog(false)
        }}
        onConfirm={handleConfirmDeleteTimelog}
        title="Are you sure?"
        message="Are you sure you want to delete this item? This action cannot be undone."
      />
    </Box >
  );
}

export default Home;
